import { useCallback, useEffect, useState } from "react";
import { ArrowCounterClockwise, LockSimple, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const AUDIENCES = [
  ["everyone", "Everyone"],
  ["client", "Only when the client answers"],
  ["family", "Only when family/rep answers"],
];

export default function IntakeBankPage() {
  const [questions, setQuestions] = useState(null);
  const [editing, setEditing] = useState(null); // { key, label, options }
  const [adding, setAdding] = useState(null); // { label, type, options, section, phase, audience }
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/intake-bank").then((r) => setQuestions(r.data.questions)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveNew = async () => {
    setBusy("new");
    try {
      const body = { label: adding.label, type: adding.type, section: adding.section, phase: adding.phase, audience: adding.audience };
      if (adding.type === "select" || adding.type === "multiselect") {
        body.options = adding.options.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      await api.post("/admin/intake-bank/custom", body);
      toast.success("Question added — it appears on every in-progress intake now, and clients who already finished get flagged for follow-up");
      setAdding(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const saveEdit = async () => {
    setBusy(editing.key);
    try {
      const body = { label: editing.label };
      if (editing.options !== null) body.options = editing.options.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put(`/admin/intake-bank/${editing.key}`, body);
      toast.success("Question updated — everywhere, instantly");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const patch = async (key, body, msg) => {
    setBusy(key);
    try {
      await api.put(`/admin/intake-bank/${key}`, body);
      toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const reset = async (q) => {
    if (q.custom && !window.confirm(`Delete "${q.label}" completely? Answers already collected stay on client records.`)) return;
    setBusy(q.key);
    try {
      await api.delete(`/admin/intake-bank/${q.key}`);
      toast.success(q.custom ? "Question deleted everywhere" : "Back to the built-in wording");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const toggleOffice = async (q) => {
    setBusy(q.key);
    try {
      await api.put("/admin/intake-bank/office-only", { key: q.key, office_only: !q.office_only });
      toast.success(!q.office_only ? "Office-only — hidden from clients" : "Clients can answer this now");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  if (!questions) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const sections = Object.entries(questions.reduce((acc, q) => { (acc[q.section] = acc[q.section] || []).push(q); return acc; }, {}));
  const sectionNames = sections.map(([s]) => s);

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6" data-testid="question-bank-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Intake question bank</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">
            Every intake question lives here. Edit the wording, choose <b>who it applies to</b>, lock office-only
            questions, add new questions, or turn questions off — changes apply instantly to the office intake and the client questionnaire.
          </p>
        </div>
        <button onClick={() => setAdding({ label: "", type: "text", options: "", section: "", phase: 2, audience: "everyone" })}
          className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-stone-900"
          data-testid="qb-add-question">
          + Add a question
        </button>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="qb-add-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <p className="font-heading text-lg font-semibold text-stone-900">Add a new question</p>
            <p className="mt-1 text-xs text-stone-500">It goes live everywhere immediately. Clients who already submitted their questionnaire get a follow-up flag for it.</p>
            <label className="mt-4 block text-xs font-semibold text-stone-600">Question wording</label>
            <textarea value={adding.label} onChange={(e) => setAdding((s) => ({ ...s, label: e.target.value }))} rows={2}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="qb-add-label" autoFocus />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600">Answer type</label>
                <select value={adding.type} onChange={(e) => setAdding((s) => ({ ...s, type: e.target.value }))}
                  className="mt-1 h-9 w-full rounded-lg border border-stone-300 px-2 text-sm" data-testid="qb-add-type">
                  {[["text", "Short answer"], ["textarea", "Long answer"], ["date", "Date"], ["select", "Pick one"], ["multiselect", "Check all that apply"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600">Who answers it</label>
                <select value={adding.audience} onChange={(e) => setAdding((s) => ({ ...s, audience: e.target.value }))}
                  className="mt-1 h-9 w-full rounded-lg border border-stone-300 px-2 text-sm" data-testid="qb-add-audience">
                  {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            {(adding.type === "select" || adding.type === "multiselect") && (
              <>
                <label className="mt-3 block text-xs font-semibold text-stone-600">Answer choices (one per line)</label>
                <textarea value={adding.options} onChange={(e) => setAdding((s) => ({ ...s, options: e.target.value }))} rows={4}
                  className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="qb-add-options" />
              </>
            )}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600">Section</label>
                <input value={adding.section} onChange={(e) => setAdding((s) => ({ ...s, section: e.target.value }))}
                  list="qb-section-names" placeholder="Additional questions"
                  className="mt-1 h-9 w-full rounded-lg border border-stone-300 px-2 text-sm" data-testid="qb-add-section" />
                <datalist id="qb-section-names">{sectionNames.map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600">Phase</label>
                <select value={adding.phase} onChange={(e) => setAdding((s) => ({ ...s, phase: Number(e.target.value) }))}
                  className="mt-1 h-9 w-full rounded-lg border border-stone-300 px-2 text-sm" data-testid="qb-add-phase">
                  <option value={2}>2 — everything else</option>
                  <option value={1}>1 — the essentials</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAdding(null)} className="h-9 rounded-full border border-stone-300 px-4 text-sm" data-testid="qb-add-cancel">Cancel</button>
              <button onClick={saveNew} disabled={busy === "new" || !adding.label.trim()}
                className="h-9 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid="qb-add-save">
                {busy === "new" ? "Saving…" : "Save question"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="qb-edit-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{editing.section}</p>
            <label className="mt-3 block text-xs font-semibold text-stone-600">Question wording</label>
            <textarea value={editing.label} onChange={(e) => setEditing((s) => ({ ...s, label: e.target.value }))} rows={3}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="qb-edit-label" />
            {editing.options !== null && (
              <>
                <label className="mt-3 block text-xs font-semibold text-stone-600">Answer choices (one per line)</label>
                <textarea value={editing.options} onChange={(e) => setEditing((s) => ({ ...s, options: e.target.value }))} rows={6}
                  className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="qb-edit-options" />
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-9 rounded-full border border-stone-300 px-4 text-sm" data-testid="qb-edit-cancel">Cancel</button>
              <button onClick={saveEdit} disabled={busy === editing.key}
                className="h-9 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-stone-900" data-testid="qb-edit-save">
                {busy === editing.key ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {sections.map(([section, qs]) => (
        <div key={section} className="mt-8" data-testid={`qb-section-${section.replace(/\W+/g, "-").toLowerCase()}`}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">{section}</p>
          <div className="divide-y divide-stone-100 rounded-2xl border border-stone-200 bg-white shadow-sm">
            {qs.map((q) => (
              <div key={q.key} className={`px-5 py-3.5 ${q.active === false ? "opacity-45" : ""}`} data-testid={`qb-row-${q.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-stone-900">
                      {q.label}
                      {q.edited && !q.custom && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700">edited</span>}
                      {q.custom && <span className="ml-2 rounded-full bg-[#4A235A]/10 px-2 py-0.5 text-[9px] font-bold text-[#4A235A]">added by you{q.added ? ` · ${q.added}` : ""}</span>}
                    </p>
                    <p className="mt-0.5 text-[10px] text-stone-400">
                      phase {q.phase} · {q.type}{q.show_if ? ` · only if ${q.show_if.key} ${q.show_if.in ? "= " + q.show_if.in.join("/") : q.show_if.contains ? "includes " + q.show_if.contains : q.show_if.contains_any ? "includes one of " + q.show_if.contains_any.join("/") : "≠ " + (q.show_if.not_in || []).join("/")}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <select value={q.audience || "everyone"} disabled={busy === q.key}
                      onChange={(e) => patch(q.key, { audience: e.target.value }, "Audience updated")}
                      className="h-8 rounded-full border border-stone-300 bg-white px-2 text-[11px] font-medium text-stone-600"
                      data-testid={`qb-audience-${q.key}`}>
                      {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button onClick={() => toggleOffice(q)} disabled={busy === q.key}
                      title={q.office_only ? "Office-only — hidden from the client questionnaire" : "Clients can answer this"}
                      className={`inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-[11px] font-semibold transition-colors ${q.office_only ? "border-[#4A235A]/40 bg-[#4A235A]/10 text-[#4A235A]" : "border-stone-200 text-stone-400 hover:text-stone-600"}`}
                      data-testid={`qb-lock-${q.key}`}>
                      <LockSimple size={11} weight={q.office_only ? "fill" : "regular"} /> {q.office_only ? "office" : "client ok"}
                    </button>
                    <button onClick={() => setEditing({ key: q.key, section: q.section, label: q.label, options: q.options ? q.options.filter(Boolean).join("\n") : null })}
                      className="inline-flex h-8 items-center gap-1 rounded-full border border-stone-200 px-2.5 text-[11px] font-semibold text-stone-500 transition-colors hover:bg-stone-900 hover:text-white"
                      data-testid={`qb-edit-${q.key}`}>
                      <PencilSimple size={11} /> Edit
                    </button>
                    <button onClick={() => patch(q.key, { active: q.active === false }, q.active === false ? "Question turned back on" : "Question hidden everywhere")}
                      className="h-8 rounded-full border border-stone-200 px-2.5 text-[11px] font-semibold text-stone-500 transition-colors hover:bg-stone-900 hover:text-white"
                      data-testid={`qb-active-${q.key}`}>
                      {q.active === false ? "Turn on" : "Turn off"}
                    </button>
                    {q.edited && (
                      <button onClick={() => reset(q)} title={q.custom ? "Delete this question everywhere" : "Back to the built-in wording"}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 text-stone-400 transition-colors hover:bg-stone-900 hover:text-white"
                        data-testid={`qb-reset-${q.key}`}>
                        <ArrowCounterClockwise size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
